import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { endpointFundGET } from '~/api/def/endpoints/fundEndpoint';
import { endpointJurisdictionsGET } from '~/api/def/endpoints/jurisdictionEndpoint';
import {
  COMPANY_STATUS,
  INVESTMENT_ROUNDS,
  portfolioCompanyPostPayloadSchema,
} from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { endpointSectorGET } from '~/api/def/endpoints/sectorEndpoint';
import { FormLabelInGrid } from '~/components/forms/FormLabelInGrid';
import { BqFormErrorText } from '~/components/ui/misc/bqTag/BqFormErrorText';
import { CompaniesHouseImport, ShareholderRow } from './CompaniesHouseImport';

import { format } from 'date-fns';
import { useCallback, useState } from 'react';

export const date2ApiString = (date: Date) => {
  return format(date, 'yyyy-MM-dd');
};

export type CreatePortfolioCompanyValues = z.infer<
  typeof portfolioCompanyPostPayloadSchema
>;

export type OnSubmit = (
  data: CreatePortfolioCompanyValues & { logoFile?: File } & {
    shareholders: ShareholderRow[];
  },
) => Promise<void> | void;

export type CreatePortfolioCompanyProps = {
  onSubmit: OnSubmit;
  isLoading: boolean;
};

export function CreatePortfolioCompany({
  onSubmit,
  isLoading,
}: CreatePortfolioCompanyProps) {
  const [shareholders, setShareholders] = useState<ShareholderRow[]>([]);

  const { data: sectorsData, isLoading: isSectorsLoading } = useQuery({
    queryKey: ['portfolioSectorsGet'],
    queryFn: endpointSectorGET,
  });

  const { data: fundsData, isLoading: isFundsLoading } = useQuery({
    queryKey: ['portfolioFundsGet'],
    queryFn: () => endpointFundGET(),
  });

  const { data: jurisdictionsData, isLoading: isJurisdictionsLoading } =
    useQuery({
      queryKey: ['portfolioJurisdictionsGet'],
      queryFn: () => endpointJurisdictionsGET(),
    });

  const { control, handleSubmit, setValue } = useForm<
    CreatePortfolioCompanyValues & { logoFile: File }
  >({
    resolver: zodResolver(portfolioCompanyPostPayloadSchema.passthrough()),
  });

  const companyName = useWatch({
    control,
    name: 'name',
  });

  const handleFormSubmit = async (
    data: CreatePortfolioCompanyValues & { logoFile: File },
  ) => {
    await onSubmit({ ...data, shareholders });
  };

  const handleImportSuccess = useCallback(
    (
      companyName: string,
      _companyNumber: string,
      shareholders: ShareholderRow[],
    ) => {
      setValue('name', companyName);
      setShareholders(shareholders);
    },
    [setValue, setShareholders],
  );

  return (
    <div className="flex flex-row justify-content-start">
      <div className="flex-initial" style={{ maxWidth: '50%' }}>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex flex-column gap-2"
        >
          <div className="grid">
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Company Name is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Company Name"
                  />
                  <div className="col col-9 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="shortName"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid htmlFor={field.name} label="Short Name" />
                  <div className="col col-9 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="currentRound"
              control={control}
              rules={{ required: 'Current Round is required.' }}
              defaultValue={INVESTMENT_ROUNDS[0]}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Current Round"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      options={INVESTMENT_ROUNDS.map((round) => ({
                        label: round,
                        value: round,
                      }))}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="companyStatus"
              control={control}
              rules={{ required: 'Company Status is required.' }}
              defaultValue={Object.values(COMPANY_STATUS)[0]}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Company Status"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      options={Object.values(COMPANY_STATUS).map((status) => ({
                        label: status,
                        value: status,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="sectorId"
              control={control}
              rules={{ required: 'Select Sector.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Sector"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id="sectorId"
                      value={field.value}
                      className="w-16rem"
                      loading={isSectorsLoading}
                      filter
                      options={sectorsData?.map((sector) => ({
                        label: sector.name,
                        value: sector.id,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="jurisdictionId"
              control={control}
              defaultValue={
                jurisdictionsData?.data.find((j) => j.abbreviation === 'GB')?.id
              }
              rules={{ required: 'Select Jurisdiction.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Jurisdiction"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id="jurisdictionId"
                      value={field.value}
                      className="w-16rem"
                      loading={isJurisdictionsLoading}
                      filter
                      options={jurisdictionsData?.data.map((jurisdiction) => ({
                        label: jurisdiction.name,
                        value: jurisdiction.id,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="fundId"
              control={control}
              rules={{ required: 'Select Fund.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid required htmlFor={field.name} label="Fund" />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id="fundId"
                      value={field.value}
                      className="w-16rem"
                      loading={isFundsLoading}
                      filter
                      options={fundsData?.data.map((fund) => ({
                        label: fund.name,
                        value: fund.id,
                      }))}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="incorporationDate"
              control={control}
              rules={{ required: 'Select incorporation date.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Incorporation Date"
                  />
                  <div className="col col-9 text-left">
                    <Calendar
                      id={field.name}
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(e) => {
                        field.onChange(e?.value ? date2ApiString(e.value) : '');
                      }}
                      showIcon
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="closeDate"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required={false}
                    htmlFor={field.name}
                    label="Close Date"
                  />
                  <div className="col col-9 text-left">
                    <Calendar
                      id={field.name}
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(e) => {
                        field.onChange(e?.value ? date2ApiString(e.value) : '');
                      }}
                      showIcon
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="logoFile"
              control={control}
              render={({ field }) => (
                <>
                  <FormLabelInGrid htmlFor={field.name} label="Company Logo" />
                  <div className="col col-9 text-left">
                    {field.value && !(field.value instanceof File) && (
                      <div className="mb-2">
                        <img
                          src={field.value}
                          alt="Current logo"
                          style={{ maxWidth: '100px' }}
                        />
                      </div>
                    )}
                    {field.value instanceof File && (
                      <div className="mb-2">
                        <img
                          src={URL.createObjectURL(field.value)}
                          alt="Preview"
                          style={{ maxWidth: '100px' }}
                        />
                      </div>
                    )}
                    <div className="flex align-items-center">
                      <FileUpload
                        id={field.name}
                        mode="basic"
                        name="logoFile"
                        accept="image/*"
                        maxFileSize={1000000}
                        chooseLabel="Choose Logo"
                        auto={false}
                        customUpload={true}
                        onSelect={(e) => {
                          if (e.files && e.files.length > 0) {
                            const selectedFile = e.files[0];
                            field.onChange(selectedFile);
                          }
                        }}
                        onRemove={() => field.onChange(null)}
                        onClear={() => field.onChange(null)}
                      />
                      <Button
                        type="button"
                        className="ml-2"
                        onClick={() => field.onChange(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </>
              )}
            />

            <div className="col col-3 text-right pt-3"></div>
            <div className="col col-9 text-left">
              <Button loading={isLoading} disabled={isLoading}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
      <div className="flex-initial flex-grow-1">
        <CompaniesHouseImport
          onImportSuccess={handleImportSuccess}
          companyName={companyName}
        />
      </div>
    </div>
  );
}
